import {
  LAnimatedCardsSection,
  LCardsSlider,
  LDarkImageContent,
  LDarkSimpleWithBackground,
  LDarkWithTextBox,
  LFooter,
  LHeader,
  LHeroSection,
  LHeroSectionTwo,
  LIconCardsSection,
  LImageCardsSection,
  LLigghtWithTopImage,
  LPriceSection,
  LReviewsSection,
  LRichText,
  LScrollableListSection,
  LSingleTitleSection,
  LTextImageSection,
  LVideoSection,
  WAccordion,
  LFormGroup,
} from "@/components";
import { HeroWithSlide } from "@/components/HeroWithSlide";
import { SlideItemProps } from "@/components/HeroWithSlide/types";
import ImageGallery from "@/features/ImageGallery/ImageGallery";
import { ImageGalleryItemProps } from "@/features/ImageGallery/types";
import {
  ComponentCmsDataPriceItem,
  Enum_Componentcmsdatatextandimagesection_Variation,
  ComponentCmsDataFormGroup,
} from "@/generated";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import { FC, ReactNode } from "react";

type ComponentCheckerProps = {
  component: any;
  urlParams?: string;
};

export const ComponentChecker: FC<ComponentCheckerProps> = ({
  component,
  urlParams,
}) => {
  switch (component?.__typename) {
    case "ComponentCmsDataHeaderSection":
      return (
        <LHeader
          image={{
            url: component?.image?.data?.attributes?.url || "",
            altTag: component?.image?.data?.attributes?.alternativeText || "",
          }}
          button={{
            text: component?.HeaderSectionButton?.text || "",
            url: component?.HeaderSectionButton?.url || "",
          }}
          helloBarContent={component?.helloBarContent || ""}
        />
      );

    case "ComponentCmsDataHeroSection":
      return (
        <LHeroSection
          key={`${component?.__typename}-${component?.id}`}
          topTitle={component?.HeroSectionTopTitle || ""}
          mainTitle={component?.mainTitle || ""}
          secondaryTitle={component?.DataHeroSectionSecondaryTitle || ""}
          image={{
            url: component?.image?.data?.attributes?.url || "",
            altTag: component?.image?.data?.attributes?.alternativeText || "",
          }}
          mobileImage={{
            url: component?.mobileImage?.data?.attributes?.url || "",
            altTag:
              component?.mobileImage?.data?.attributes?.alternativeText || "",
          }}
          button={{
            text: component?.HeroSectionButton?.text || "",
            url: component?.HeroSectionButton?.url || "",
            urlParams: urlParams || "",
          }}
          countDownTimer={{
            endDate: new Date(component?.countDownTimer?.endDate),
            description: component?.countDownTimer?.description || "",
          }}
          bgColor={component?.bgColor || ""}
          box={component?.box || false}
          boxColor={component?.boxColor || ""}
          textColor={component?.textColor || ""}
          textShadow={component?.textShadow || false}
        />
      );

    case "ComponentCmsDataHeroSectionTwo":
      return (
        <LHeroSectionTwo
          key={`${component?.__typename}-${component?.id}`}
          topText={component?.topText || ""}
          mainTitle={component?.HeroSectionTwoMainTitle}
          image={{
            url: component?.HeroSectionTwoImage?.data?.attributes?.url || "",
            altTag:
              component?.HeroSectionTwoImage?.data?.attributes
                ?.alternativeText || "",
          }}
          button={{
            text: component?.button?.text || "",
            url: component?.button?.url?.includes("#")
              ? component?.button?.url
              : `${component?.button?.url}${urlParams}`,
          }}
          bottomText={component?.bottomText || ""}
          textAfterButton={component?.textAfterButton || false}
          belowTitleText={component?.belowTitle || ""}
        />
      );
    case "ComponentCmsDataIconCardsSection":
      return (
        <LIconCardsSection
          key={`${component?.__typename}-${component?.id}`}
          topTitle={component?.IconCardsSectionTopTitle}
          mainTitle={component?.mainTitle}
          subTitle={component?.subTitle}
          iconCards={
            component?.iconCards?.map((icon: any) => ({
              iconName: icon?.iconName as IconName,
              title: icon?.title,
              large: icon?.large,
              subTitle: icon?.subTitle,
            })) as {
              iconName: IconName;
              title: string;
              subTitle?: string;
              large?: boolean;
            }[]
          }
          showAppButton={component?.showAppButton || false}
          hasExclamationMark={component?.hasExclamationMark || false}
          centerText={component?.centerText || false}
          buttons={
            component?.IconCardsSectionButtons?.map((button: any) => ({
              text: button?.text,
              url: `${button?.url}${urlParams}`,
            })) as {
              text: string;
              url: string;
            }[]
          }
          backgroundColor={component?.backgroundColor || ""}
        />
      );

    case "ComponentCmsDataTextAndImageSection":
      return (
        <LTextImageSection
          key={`${component?.__typename}-${component?.id}`}
          topTitle={component?.topTitle || ""}
          topTitleColor={component?.topTitleColor || ""}
          mainTitle={component?.mainTitle}
          mainTitleColor={component?.mainTitleColor}
          textContent={component?.DataTextAndImageSectionTextContent}
          textContentColor={component?.DataTextAndImageSectionTextContentColor}
          imageMask={{
            url:
              component?.imageMask?.data?.attributes?.url ||
              component?.image?.data?.attributes?.url ||
              "",
            altTag: component?.image?.data?.attributes?.alternativeText || "",
          }}
          backgroundImageMask={{
            url: component?.imageBackgroundMask?.data?.attributes?.url || "",
            altTag:
              component?.imageBackgroundMask?.data?.attributes
                ?.alternativeText || "",
          }}
          variation={
            (component?.variation as Enum_Componentcmsdatatextandimagesection_Variation) ||
            null
          }
          shadow={component?.shadow || false}
          rounded={component?.rounded || false}
          backgroundColor={component?.backgroundColor || ""}
          buttons={
            component?.TextAndImageSectionButton?.map((button: any) => ({
              text: button?.text,
              url: button?.url?.includes("#")
                ? button?.url
                : `${button?.url}${urlParams}`,
              color: button?.color,
              textColor: button?.textColor,
            })) as {
              text: string;
              url: string;
            }[]
          }
          countDownTimer={{
            endDate: component?.countDownTimer?.endDate,
            description: component?.countDownTimer?.description || "",
          }}
          reverse={component?.reverse || false}
          underTextImage={{
            url: component?.underTextImage?.data?.attributes?.url || "",
            altTag:
              component?.underTextImage?.data?.attributes?.alternativeText ||
              "",
          }}
          hideImageOnMobile={component?.hideImageOnMobile || false}
          maskImage={component?.maskImage || false}
          bigImageOnMobile={component?.bigImageOnMobile || false}
        />
      );

    case "ComponentCmsDataDarkTextBoxSection":
      return (
        <LDarkWithTextBox
          key={`${component?.__typename}-${component?.id}`}
          topTitle={component?.topTitle || ""}
          topTitleColor={component?.topTitleColor || "primary"}
          mainTitle={component?.mainTitle}
          mainTitleColor={component?.mainTitleColor || "primary"}
          backgroundImageUrl={component?.backgroundImage?.data?.attributes?.url}
          mobileBackgroundImage={
            component?.mobileBackgroundImage?.data?.attributes?.url
          }
          bottomTitle={component?.bottomTitle || ""}
          boxText={component?.boxText || ""}
          boxTitle={component?.boxTitle || ""}
          button={{
            color: component?.DarkTextBoxSectionButton?.color || "",
            textColor: component?.DarkTextBoxSectionButton?.textColor || "",
            text: component?.DarkTextBoxSectionButton?.text || "",
            url: component?.DarkTextBoxSectionButton?.url?.includes("#")
              ? component?.DarkTextBoxSectionButton?.url
              : `${component?.DarkTextBoxSectionButton?.url}${urlParams}`,
          }}
          centerText={component?.centerText || false}
          box={component?.box || false}
          boxColor={component?.boxColor || ""}
          boxTitleColor={component?.boxTitleColor || ""}
          boxTextColor={component?.boxTextColor || ""}
          buttonTitleColor={component?.buttonTitleColor || ""}
        />
      );

    case "ComponentCmsDataScrollListSection":
      return (
        <LScrollableListSection
          key={`${component?.__typename}-${component?.id}`}
          topTitle={component?.topTitle || ""}
          mainTitle={component?.mainTitle || ""}
          secondaryTitle={component?.ScrollListSectionSecondaryTitle || ""}
          backgroundImageUrl={component?.backgroundImage?.data?.attributes?.url}
          bottomSectionImgUrl={
            component?.bottomSectionImage?.data?.attributes?.url
          }
          bottomSectionTitle={component?.bottomSectionTitle || ""}
          button={{
            text: component?.ScrollListSectionButton?.text || "",
            url: component?.ScrollListSectionButton?.url?.includes("#")
              ? component?.ScrollListSectionButton?.url
              : `${component?.ScrollListSectionButton?.url}${urlParams}`,
          }}
          boxes={
            component?.boxes?.map((box: any) => ({
              boxTitle: box?.boxTitle,
              boxText: box?.boxText,
              bottomText: box?.bottomText,
              listTitle: box?.listTitle,
              boxCards: box?.boxCards?.map((card: any) => ({
                title: card?.title,
                imgUrl: card?.image?.data?.attributes?.url,
                altTag: card?.image?.data?.attributes?.alternativeText,
                text: card?.text,
                showMoreText: card?.showMoreText,
              })),
            })) as {
              boxTitle: string;
              boxText: string; // rich text
              listTitle?: string;
              bottomText?: string;
              boxCards?: {
                title: string;
                imgUrl?: string;
                altTag?: string;
                text?: string;
                showMoreText?: string; //rich text
              }[];
            }[]
          }
          countDownTimer={{
            endDate: component?.countDownTimer?.endDate,
            description: component?.countDownTimer?.description || "",
          }}
        />
      );

    case "ComponentCmsDataAnimatedCardSection":
      return (
        <LAnimatedCardsSection
          key={`${component?.__typename}-${component?.id}`}
          topTitle={component?.topTitle || ""}
          mainTitle={component?.AnimatedCardSectionMainTitle}
          secondarytitle={
            component?.DataAnimatedCardSectionSecondaryTitle || ""
          }
          firstSectionImages={
            component?.firstSectionImages?.data?.map((image: any) => ({
              imgageUrl: image?.attributes?.url,
              altTag: image?.attributes?.alternativeText,
              modalText: image?.attributes?.caption,
            })) as {
              imgageUrl: string;
              altTag: string;
              modalText?: string;
            }[]
          }
          secondSectionImages={
            component?.secondSectionImages?.data?.map((image: any) => ({
              imgageUrl: image?.attributes?.url,
              altTag: image?.attributes?.alternativeText,
              modalText: image?.attributes?.caption,
            })) as {
              imgageUrl: string;
              altTag: string;
              modalText?: string;
            }[]
          }
          button={{
            text: component?.button?.text || "",
            url: component?.button?.url?.includes("#")
              ? component?.button?.url
              : `${component?.button?.url}${urlParams}`,
          }}
        />
      );

    case "ComponentCmsDataLightTopImgSection":
      return (
        <LLigghtWithTopImage
          key={`${component?.__typename}-${component?.id}`}
          mainTitle={component?.LightTopImgSectionMainTitle || ""}
          textContent={component?.DataLightTopImgSectionTextContent || ""}
          topTitle={component?.topTitle || ""}
          topImage={{
            imgUrl: component?.topImage?.data?.attributes?.url || "",
            altTag:
              component?.topImage?.data?.attributes?.alternativeText || "",
          }}
          centerText={component?.centerText || false}
          countDownTimer={{
            endDate: new Date(component?.countDownTimer?.endDate),
            description: component?.countDownTimer?.description || "",
          }}
        />
      );

    case "ComponentCmsDataDarkBgImageSection":
      return (
        <LDarkSimpleWithBackground
          key={`${component?.__typename}-${component?.id}`}
          image={{
            url: component?.image?.data?.attributes?.url || "",
            altTag: component?.image?.data?.attributes?.alternativeText || "",
          }}
          backgroundImageUrl={component?.backgroundImage?.data?.attributes?.url}
          mainTitle={component?.mainTitle}
          secondaryTitle={component?.DarkBgImageSectionSecondaryTitle}
          button={{
            text: component?.button?.text || "",
            url: component?.button?.url?.includes("#")
              ? component?.button?.url
              : `${component?.button?.url}${urlParams}`,
          }}
          centerText={component?.centerText || false}
          countDownTimer={{
            endDate: new Date(component?.countDownTimer?.endDate),
            description: component?.countDownTimer?.description || "",
          }}
        />
      );

    case "ComponentCmsDataFooterSection":
      return (
        <LFooter
          key={`${component?.__typename}-${component?.id}`}
          leftText={component?.leftText}
          rightText={component?.rightText || ""}
          bottomContent={component?.bottomContent || ""}
          iconColumnTitle={component?.iconColumnTitle || ""}
          linkWithIcon={
            component?.iconLinks?.map((iconLink: any) => ({
              iconName: iconLink?.iconName,
              text: iconLink?.text,
              link: iconLink?.link,
            })) as {
              iconName?: string;
              text?: string;
              link?: string;
            }[]
          }
          column={
            component?.columns?.map((column: any) => ({
              title: column?.title,
              content: column?.content,
            })) as {
              title?: string;
              content?: string; //rich text
            }[]
          }
        />
      );

    case "ComponentCmsDataSlider":
      return (
        <LCardsSlider
          key={`${component?.__typename}-${component?.id}`}
          title={component?.title || ""}
          subTitle={component?.DataSliderSubTitle || ""}
          popup={component?.popup}
          popupRichText={component?.popupRichText}
          type={component?.type}
          cards={
            component?.items?.map((card: any) => ({
              isOnLanding: true,
              underText: card?.underText,
              subtitleOverImage: card?.subtitleOverImage,
              url: card?.link,
              title: card?.title,
              description: card?.body,
              titleNrOfChars: card?.titleNrOfChars,
              underTextNrOfChars: card?.underTextNrOfChars,
              bodyNrOfChars: card?.bodyNrOfChars,
              image: {
                imageUrl: card?.image?.data?.attributes?.url,
                altTag: card?.image?.data?.attributes?.alternativeText,
              },
              frameColor: card?.frameColor,
              titleColor: card?.titleColor,
              bodyColor: card?.bodyColor,
              underTextColor: card?.underTextColor,
            })) as {
              titleNrOfChars?: number | null;
              underTextNrOfChars?: number | null;
              bodyNrOfChars?: number | null;
              image: {
                imageUrl: string;
                altTag: string;
              };
              title?: string;
              description?: string;
              url?: string;
              isOnLanding?: boolean;
              subtitleOverImage?: boolean;
              underText?: string;
              frameColor?: string;
              titleColor?: string;
              bodyColor?: string;
              underTexColor?: string;
            }[]
          }
          centerText={component?.centerText || false}
        />
      );

    case "ComponentCmsDataFaq":
      return (
        <WAccordion
          key={`${component?.__typename}-${component?.id}`}
          isOnLanding
          title={component?.title || ""}
          tabs={
            component?.items?.map((faqItem: any) => ({
              title: faqItem?.question,
              content: faqItem?.answer,
            })) as {
              title: string;
              subtitle?: string;
              content: ReactNode;
            }[]
          }
        />
      );

    case "ComponentCmsDataRichText":
    case "ComponentCmsDataRichTextHtml":
      return (
        <LRichText
          key={`${component?.__typename}-${component?.id}`}
          type={component?.__typename}
          richText={component?.text || ""}
          centerText={component?.center || false}
          fullWidth={component?.fullWidth || false}
          backgroundImage={{
            url: component?.backgroundImage?.data?.attributes?.url || "",
            altTag:
              component?.backgroundImage?.data?.attributes?.alternativeText ||
              "",
          }}
        />
      );

    case "ComponentCmsDataImageCardsSection":
      return (
        <LImageCardsSection
          key={`${component?.__typename}-${component?.id}`}
          title={component?.ImageCardsSectiontitle || ""}
          backgroundColor={component?.backgroundColor || ""}
          cards={
            component?.items?.map((card: any) => ({
              large: card?.large,
              title: card?.title,
              text: card?.text,
              textColor: card?.textColor,
              backgroundColor: card?.backgroundColor,
              button: {
                url: card?.buttonUrl,
                text: card?.buttonText,
              },
              logo: {
                url: card?.logo?.data?.attributes?.url,
                altTag: card?.logo?.data?.attributes?.alternativeText,
              },
              image: {
                url: card?.picture?.data?.attributes?.url,
                altTag: card?.picture?.data?.attributes?.alternativeText,
                mime: card?.picture?.data?.attributes?.mime,
              },
            })) as {
              large?: boolean;
              image?: {
                url: string;
                altTag: string;
                mime: string;
              };
              logo?: {
                url: string;
                altTag: string;
              };
              text?: string;
              title?: string;
              button?: {
                text: string;
                url: string;
              };
            }[]
          }
        />
      );

    case "ComponentCmsDataSingleTitleSection":
      return (
        <LSingleTitleSection
          key={`${component?.__typename}-${component?.id}`}
          id={component?.id || ""}
          topTitle={component?.topTitle || ""}
          mainTitle={component?.SingleTitleMainTitle || ""}
          subTitle={component?.SingleTitleSubTitle || ""}
          center={component?.center || false}
          backgroundColor={component?.backgroundColor || ""}
          textColor={component?.textColor || ""}
        />
      );

    case "ComponentCmsDataReviewsSection":
      return (
        <LReviewsSection
          key={`${component?.__typename}-${component?.id}`}
          topTitle={component?.topTitle || ""}
          description={component?.description || ""}
          mainTitle={component?.ReviewSectionMainTitle || ""}
          size={component?.size || "big"}
          hideImage={component?.hideImage || false}
          box={component?.box || false}
          boxColor={component?.boxColor || ""}
          textColor={component?.textColor || ""}
          itemBoxColor={component?.itemBoxColor || ""}
          itemBoxTextColor={component?.itemBoxTextColor || ""}
          reviewItem={
            component?.reviewItems?.map((review: any) => ({
              authorLocation: review?.authorLocation,
              authorName: review?.authorName,
              authorOccupations: review?.authorOccupations,
              authorPicture: {
                url: review?.authorPicture?.data?.attributes?.url,
                altTag:
                  review?.authorPicture?.data?.attributes?.alternativeText,
                formats: review?.authorPicture?.data?.attributes?.formats,
              },
              title: review?.title,
              content: review?.content,
              showPictureAsCircle: review?.showPictureAsCircle,
              numberOfChars: review?.contentNrOfChars,
            })) as {
              title?: string;
              content?: string; //rich text
              authorPicture?: {
                url: string;
                altTag: string;
              };
              authorName?: string;
              authorLocation?: string;
              authorOccupations?: string;
              showPictureAsCircle?: boolean;
            }[]
          }
        />
      );

    case "ComponentCmsDataPriceSection":
      return (
        <LPriceSection
          key={`${component?.__typename}-${component?.id}`}
          topTitle={component?.topTitle || ""}
          topTitleColor={component?.topTitleColor || ""}
          mainTitle={component?.PriceSectionMainTitle || ""}
          mainTitleColor={component?.mainTitleColor || ""}
          description={component?.description || ""}
          descriptionColor={component?.descriptionColor || ""}
          priceItems={component?.priceItems as ComponentCmsDataPriceItem[]}
          showAsSlider={component?.showAsSlider || false}
          showAsSliderWeb={component?.showAsSliderWeb || false}
          priceItemsPerSlideWeb={component?.priceItemsPerSlideWeb || 0}
          sectionId={component?.sectionId || ""}
          countDownTimer={{
            endDate: new Date(component?.countDownTimer?.endDate),
            description: component?.countDownTimer?.description || "",
          }}
          urlParams={urlParams || ""}
        />
      );

    case "ComponentCmsDataVideoSection":
      return (
        <LVideoSection
          key={`${component?.__typename}-${component?.id}`}
          videoUrl={component?.video?.data?.attributes?.url || ""}
          mobileVideoUrl={component?.videoMobile?.data?.attributes?.url || ""}
        />
      );

    case "ComponentCmsDataDarkImageContent": {
      return (
        <LDarkImageContent
          key={`${component?.__typename}-${component?.id}`}
          backgroundImage={component?.backgroundImage?.data?.attributes?.url}
          countDownTimer={{
            endDate: component?.countDownTimer?.endDate,
            description: component?.countDownTimer?.description || "",
          }}
        />
      );
    }

    case "ComponentCmsDataScrollToSection": {
      return (
        <div
          key={`${component?.__typename}-${component?.id}`}
          id={component?.sectionId || ""}
        />
      );
    }

    case "ComponentCmsDataHeroSectionSlider": {
      return (
        <HeroWithSlide
          key={`${component?.__typename}-${component?.id}`}
          autoPlay={component?.autoPlay || false}
          items={
            component?.heroSectionSliderItems?.map((item: any) => ({
              topTitle: item?.topTitle,
              mainTitle: item?.mainTitle,
              secondaryTitle: item?.secondaryTitle,
              image: {
                url: item?.image?.data?.attributes?.url,
                altTag: item?.image?.data?.attributes?.alternativeText,
                mime: item?.image?.data?.attributes?.mime,
              },
              mobileImage: {
                url: item?.mobileImage?.data?.attributes?.url,
                altTag: item?.mobileImage?.data?.attributes?.alternativeText,
                mime: item?.mobileImage?.data?.attributes?.mime,
              },
              button: item?.button,
              box: item?.box,
              boxColor: item?.boxColor,
              textColor: item?.textColor,
              textShadow: item?.textShadow,
            })) as SlideItemProps[]
          }
        />
      );
    }

    case "ComponentCmsDataImageGallery": {
      return (
        <ImageGallery
          key={`${component?.__typename}-${component?.id}`}
          noScroll={component?.noScroll || false}
          backgroundColor={component?.backgroundColor || ""}
          items={
            component?.items?.map((item: any) => ({
              title: item?.ComponentCmsDataImageGalleryTitle || "",
              category: item?.category || "",
              imageUrl: item?.image?.data?.attributes?.url || "",
              imageAlt: item?.image?.data?.attributes?.alternativeText || "",
            })) as ImageGalleryItemProps[]
          }
        />
      );
    }

    case "ComponentCmsDataCaptureContact": {
      return (
        <LFormGroup
          key={`${component?.__typename}-${component?.id}`}
          data={component as unknown as ComponentCmsDataFormGroup}
        />
      );
    }

    default:
      return "";
  }
};
